import React, { Key, Ref, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Cascader } from 'antd';
import {
  Paging,
  Table,
  usePaging,
  useAsync,
  Form,
  Input,
  CustomFilter,
  EllipsisSpan,
  Select,
} from '@maxtropy/components';
import { formatOptionData, getChildNodesByParentIds } from '@/shared/components/CascadingMultipleSelector/utils';
import { useRequest } from 'ahooks';
import { DefaultOptionType } from 'antd/es/cascader';
import { useQuery } from '../../../utils/utils';
import styles from '../index.module.scss';
import { Device, DeviceListRequest, getDeviceList } from '@/api/device';
import { getLightDeviceTypeTree, getWorkSpaceLightDeviceTypeTree } from '@/api/deviceType';
import { apiV2ComprehensiveLabelListPost } from '@maxtropy/device-customer-apis-v2';

interface FilterParams {
  name?: string | undefined;
  tag?: string | undefined;
  typeId?: Array<Array<number>> | undefined;
}

export interface DeviceModalRef {
  selectedRowKeys?: Key[];
}

type SearchParams = Omit<DeviceListRequest, 'page' | 'size' | 'gatewayId' | 'iotProtocol'>;

interface DeviceModalProps {
  bindDeviceIds?: Key[];
  defaultTypeIds?: number[];
  hideTagFilter?: boolean;
  fixedTypeLabel?: string;
}

const normalizeKey = (key: Key): Key => {
  if (typeof key === 'string' && /^\d+$/.test(key)) return Number(key);
  return key;
};

const columns = () => [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备S/N码',
    dataIndex: 'sn',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备标签',
    dataIndex: 'tags',
    ellipsis: { showTitle: true },
    render: (v?: string[]) => <EllipsisSpan value={v ? v.join(',') : '--'} />,
  },
  {
    title: '所属组织',
    dataIndex: 'customerName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

const DeviceModal = React.forwardRef(
  ({ bindDeviceIds, defaultTypeIds, hideTagFilter, fixedTypeLabel }: DeviceModalProps, ref: Ref<DeviceModalRef>) => {
  const pagingInfo = usePaging();
  const [form] = Form.useForm();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>();

  const deviceTypeData = useAsync(getWorkSpaceLightDeviceTypeTree);

  const { data: labelList } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 3 }), {
    ready: !hideTagFilter,
  });

  const { data, isLoading }: { data?: Device[]; isLoading?: boolean } = useQuery(
    useCallback(async () => {
      if (!deviceTypeData) return;
      const res = await getDeviceList({
        ...searchParams,
        typeId: searchParams?.typeId ?? defaultTypeIds ?? getChildNodesByParentIds(deviceTypeData?.tree.map(i => [i.id]), deviceTypeData?.tree),
        page: pageOffset,
        size: pageSize,
      });
      if (res) setTotalCount(res.total);
      return res.list;
    }, [pageOffset, pageSize, searchParams, setTotalCount, deviceTypeData, defaultTypeIds])
  );

  useEffect(() => {
    setSelectedRowKeys((bindDeviceIds ?? []).map(normalizeKey));
  }, [bindDeviceIds]);

  useEffect(() => {
    if (!defaultTypeIds?.length || searchParams?.typeId) return;
    setSearchParams(prev => ({
      ...prev,
      typeId: defaultTypeIds,
    }));
  }, [defaultTypeIds, searchParams?.typeId]);

  const onReset = () => {
    form.resetFields();
    if (defaultTypeIds?.length) {
      setSearchParams({
        typeId: defaultTypeIds,
      });
    } else {
      setSearchParams(undefined);
    }
    form.submit();
    setPageOffset(1);
  };

  const onFinish = (val: FilterParams) => {
    const typeId = fixedTypeLabel
      ? defaultTypeIds
      : getChildNodesByParentIds(val.typeId, deviceTypeData?.tree);
    const params: SearchParams = {
      codeOrName: val.name,
      tag: val.tag,
      typeId,
    };
    setSearchParams(params);
    setPageOffset(1);
  };

  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys: selectedRowKeys,
    onChange: (selectedRowKeys: Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  useImperativeHandle(ref, () => ({
    selectedRowKeys,
  }));

  const length = [...new Set([...(bindDeviceIds ?? []), ...(selectedRowKeys ?? [])])].length;

  const filter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

  return (
    <div className={styles.deviceModal}>
      <CustomFilter<FilterParams> form={form} onFinish={val => onFinish(val as FilterParams)} onReset={onReset}>
        <Form.Item name="name" label="编号/名称">
          <Input placeholder={'请输入编号或名称查询'} style={{ width: 160 }} />
        </Form.Item>

        {fixedTypeLabel ? (
          <Form.Item label="所属类目">
            <span>{fixedTypeLabel}</span>
          </Form.Item>
        ) : (
          <Form.Item name="typeId" label="所属类目">
            <Cascader
              options={formatOptionData(deviceTypeData)}
              allowClear={false}
              fieldNames={{ children: 'child' }}
              multiple
              maxTagCount="responsive"
              showSearch={{ filter }}
              placeholder={'请选择所属类目'}
            />
          </Form.Item>
        )}

        {!hideTagFilter && (
          <Form.Item name="tag" label="标签">
            <Select
              placeholder="请选择"
              options={labelList?.list?.map(item => ({ value: item.id, label: item.name }))}
              allowClear
              mode="multiple"
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        )}
      </CustomFilter>

      <Table
        scroll={{ y: 300 }}
        rowKey="id"
        loading={isLoading}
        rowSelection={rowSelection}
        columns={columns()}
        dataSource={data}
        bottomLeftExtra={
          <div className={styles.tips}>
            <span>总计</span>
            <span className={styles.red}>{length}</span>
            <span>项</span>
          </div>
        }
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </div>
  );
  }
);

export default DeviceModal;
